import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaArrowCircleLeft, FaArrowCircleRight } from 'react-icons/fa'
import toast from 'react-hot-toast'
import PageHeader from '../../shared/PageHeader'
import { fetchData } from '../../helpers/fetchData'
import { MEDICAL_REPORT_SECTIONS, DEFAULT_MEDICAL_REPORT_SECTIONS } from '../../constants/MedicalReportSections'
import DropdownField from '../../shared/DropdownField'
import { InputSwitch } from 'primereact/inputswitch'
import Spinner from '../../shared/Spinner'
import CheckPermission from '../../hooks/CheckPermission'

const endpoint = id => `/v3/medical-report-settings/practioner/${encodeURIComponent(id)}`

export function MedicalReportSettings({ embedded = false }) {
  const { hasPermission } = CheckPermission()
  const { t, i18n } = useTranslation()
  const [practitioners, setPractitioners] = useState([])
  const [practitionerId, setPractitionerId] = useState('')
  const [settings, setSettings] = useState({ use_default: true, sections: DEFAULT_MEDICAL_REPORT_SECTIONS })
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    fetchData('POST', '/v3/practitioner/getLimitedData', {}).then(res => {
      const list = Array.isArray(res) ? res : (res?.data || [])
      setPractitioners(list)
    }).catch(() => toast.error(t('Failed to load practitioners')))
  }, [t])

  useEffect(() => {
    if (!practitionerId) return
    setLoading(true)
    fetchData('GET', endpoint(practitionerId), {}).then(res => {
      const data = res?.data?.data || res?.data || {}
      const sections = data.sections?.length ? data.sections.map(section => section?.acc || section) : DEFAULT_MEDICAL_REPORT_SECTIONS
      setSettings({ use_default: data.use_default !== false, sections })
    }).catch(() => toast.error(t('Failed to load medical Summary settings'))).finally(() => setLoading(false))
  }, [practitionerId, t])

  const save = async () => {
    if (!practitionerId) return toast.error(t('Please select a practitioner'))
    setLoading(true)
    try {
      const res = await fetchData('PUT', endpoint(practitionerId), { ...settings, practitioner_id: practitionerId })
      if (res) toast.success(t('Medical summary settings saved'))
    } catch (error) { toast.error(t('Failed to save medical summary settings')) } finally { setLoading(false) }
  }

  const toggleSection = key => setSettings(prev => ({ ...prev, use_default: false, sections: prev.sections.includes(key) ? prev.sections.filter(item => item !== key) : [...prev.sections, key] }))
  const displayName = p => p.name || p.full_name || p.name_en || p.practitioner_name || p.practitioner_id
  // Medical report settings must use the same practitioner_id stored in EMR, e.g. PRA-0002.
  const id = p => p.practitioner_id
  const practitionerOptions = practitioners.filter(p => id(p)).map(p => ({ label: displayName(p), value: id(p) }))

  const content = <section className='overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'>
    <div className='border-b border-gray-200 bg-gray-50 px-5 py-4'>
      <h2 className='text-base font-semibold text-gray-800'>{t('Medical Summary Settings')}</h2>
      <p className='mt-1 text-sm text-gray-500'>{t('Configure which medical summary sections are displayed')}</p>
    </div>
    <div className='p-5'>
      <DropdownField useFormik={false} name='practitioner_id' label={t('Practitioner')} value={practitionerId} handleChange={e => setPractitionerId(e.value)} options={practitionerOptions} placeholder={t('Select Practitioner')} filter showClear />
      <div className='flex items-center justify-between border-b py-4 mb-4'>
        <span className='text-sm font-medium'>{t('Use Default Settings')}</span>
        <InputSwitch checked={settings.use_default} onChange={e => setSettings(prev => ({ ...prev, use_default: e.value, sections: e.value ? DEFAULT_MEDICAL_REPORT_SECTIONS : prev.sections }))} disabled={!practitionerId || loading} />
      </div>
      <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3'>
        {MEDICAL_REPORT_SECTIONS.map(([key, label]) => <label key={key} className='flex items-center gap-3 border rounded p-3'><input type='checkbox' disabled={settings.use_default || !practitionerId} checked={settings.sections.includes(key)} onChange={() => toggleSection(key)} />{t(label)}</label>)}
      </div>
      <button type='button' disabled={loading || !practitionerId} onClick={save} className='mt-6 ml-auto px-5 py-2 rounded bg-primary text-white disabled:opacity-50 flex items-center justify-center gap-2 min-w-[140px]'>
        <>{loading && <Spinner width='16px' height='16px' containerClass='' />}{t('Save Settings')}</>
      </button>
    </div>
  </section>

  if (embedded) return content
  return <div className='main-page-container !overflow-y-auto scrollbar'><PageHeader title={t('Medical Summary Settings')} Icon={i18n.language === 'en' ? FaArrowCircleLeft : FaArrowCircleRight} />{content}</div>
}

export default MedicalReportSettings
